import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";

const code = `// Class-based: pair .toggle-button with .toggle-button-on for the active state
<button type="button" className="toggle-button toggle-button-on">All</button>
<button type="button" className="toggle-button">Active</button>
<button type="button" className="toggle-button">Archived</button>

// Or drive the on state from aria-pressed (preferred for screen readers)
<button type="button" className="toggle-button" aria-pressed={true}>All</button>
<button type="button" className="toggle-button" aria-pressed={false}>Active</button>
<button type="button" className="toggle-button" aria-pressed={false}>Archived</button>

// Compact variant — tighter padding, centered label, no status circle.
// Use inside dense grids of toggles (e.g. scheduling slot pickers).
<button type="button" className="toggle-button toggle-button-compact" aria-pressed={true}>8:00</button>
<button type="button" className="toggle-button toggle-button-compact" aria-pressed={false}>12:00</button>`;

function PreviewGroup() {
  const [value, setValue] = React.useState<string>("all");
  const items = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
  ];
  const [compact, setCompact] = React.useState<Set<string>>(
    () => new Set(["8:00", "16:00"]),
  );
  const compactTimes = ["8:00", "12:00", "16:00", "20:00"];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <button
            key={item.value}
            type="button"
            className="toggle-button"
            aria-pressed={value === item.value}
            onClick={() => setValue(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {compactTimes.map((t) => (
          <button
            key={t}
            type="button"
            className="toggle-button toggle-button-compact w-16 font-mono"
            aria-pressed={compact.has(t)}
            onClick={() =>
              setCompact((prev) => {
                const next = new Set(prev);
                if (next.has(t)) next.delete(t);
                else next.add(t);
                return next;
              })
            }
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ToggleButtonsSection() {
  return (
    <SectionShell
      id="toggle-buttons"
      title="Toggle buttons"
      description={
        <>
          Pill-shaped buttons with an on/off state. Use them as a small,
          inline filter or segmented control where one or more options can
          be active. Each pill renders a status circle on the left — an
          empty outline in the off state, a filled check-circle in the on
          state — so the active selection reads at a glance. Built as a
          base class so any <code>&lt;button&gt;</code> can adopt the look
          — no React primitive required.
        </>
      }
      whenToUse={
        <ul>
          <li>Filter chips above a list ("All / Active / Archived").</li>
          <li>Small segmented choices inside a toolbar.</li>
          <li>Multi-select tag pickers where each pill toggles independently.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>For primary actions — use <code>&lt;Button&gt;</code>.</li>
          <li>For yes/no settings — use a switch or checkbox.</li>
          <li>For navigation — use anchors styled as tabs.</li>
        </ul>
      }
      preview={<PreviewGroup />}
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>.toggle-button</code> — base pill (off state). Renders on
            any <code>&lt;button&gt;</code>.
          </li>
          <li>
            <code>.toggle-button-on</code> <em>or</em>{" "}
            <code>aria-pressed="true"</code> — active state. Both selectors
            are wired up; prefer <code>aria-pressed</code> for assistive
            tech.
          </li>
          <li>
            The status circle on the left is rendered automatically via a
            <code>::before</code> pseudo-element — no markup needed.
          </li>
          <li>
            <code>.toggle-button-compact</code> — tighter padding, smaller
            text, centered label, and no status circle. Reach for it when
            packing 20+ toggles into a dense grid (e.g. weekly slot
            pickers) where the active background alone is enough to read.
          </li>
          <li>
            <code>.toggle-button-with-icon</code> — hides the auto-rendered
            status circle so you can supply a domain-specific icon (channel
            logo, file-type glyph, etc.) as the first child of the button.
          </li>
          <li>
            Disabled state is handled automatically via{" "}
            <code>disabled</code>.
          </li>
        </ul>
      }
    />
  );
}
